/**
 * Studies Page
 *
 * List, search and manage medical imaging studies
 */

import { useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  FileImage,
  Search,
  Eye,
  Trash2,
  Loader2,
  AlertCircle,
  Calendar,
  Filter,
  X,
  Upload,
  User,
  UserPlus,
  Undo2,
} from 'lucide-react'

import {
  useStudies,
  useDeleteStudy,
  formatStudyDate,
  STUDY_STATUS_LABELS,
  STUDY_STATUS_COLORS,
} from '@/application/studies'
import type { StudyWithPatient, StudyStatus, StudySearchFilters } from '@/application/studies'
import { useActiveModalities } from '@/application/modalities'
import { useReturnedItems, useWorklist } from '@/application/worklist'
import { WORKLIST_STATUS_LABELS } from '@/config/constants'
import { useAuth } from '@/application/auth'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/presentation/components/ui/Table'
import { PaginationBar } from '@/presentation/components/ui/Pagination'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { UploadDicomModal, AssignStudyModal } from '@/presentation/components/studies'
import {
  PAGINATION,
  USER_ROLES,
} from '@/config/constants'

// =============================================================================
// Constants
// =============================================================================

const STATUS_OPTIONS = Object.entries(STUDY_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}))

// =============================================================================
// Helper Functions
// =============================================================================

function getStatusBadgeVariant(status: StudyStatus): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  return STUDY_STATUS_COLORS[status] || 'default'
}

// =============================================================================
// Components
// =============================================================================

interface StudyRowProps {
  study: StudyWithPatient
  onDelete: (study: StudyWithPatient) => void
  onAssign: (study: StudyWithPatient) => void
  isDeleting: boolean
  canDelete: boolean
  canAssign: boolean
  isReturned: boolean
  isAssigned: boolean
  assigneeName: string | null
}

function StudyRow({ study, onDelete, onAssign, isDeleting, canDelete, canAssign, isReturned, isAssigned, assigneeName }: StudyRowProps) {
  return (
    <TableRow className={isReturned ? 'bg-orange-50 dark:bg-orange-950/40 hover:bg-orange-100 dark:bg-orange-900/40' : ''}>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isReturned ? 'bg-orange-200' : 'bg-[var(--primary)]/10'}`}>
            {isReturned ? <Undo2 className="h-4 w-4 text-orange-600 dark:text-orange-400" /> : <User className="h-4 w-4 text-[var(--primary)]" />}
          </div>
          <div className="min-w-0">
            <Link
              to={`/studies/${study.id}`}
              className="font-medium text-[var(--foreground)] hover:text-[var(--primary)] block truncate"
            >
              {study.patientName}
            </Link>
            <p className="text-xs text-[var(--foreground-muted)]">
              {study.patientDocumentType} {study.patientDocumentNumber}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col min-w-[120px]">
          <span className="font-medium truncate">
            {study.studyDescription || 'Sin descripción'}
          </span>
          <span className="text-xs text-[var(--foreground-muted)] truncate">
            {study.accessionNumber || study.studyInstanceUID.slice(0, 16) + '...'}
          </span>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <span className="text-sm">{study.modality}</span>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <div className="flex flex-col">
          <span className="text-sm">{formatStudyDate(study.studyDate)}</span>
          <span className="text-xs text-[var(--foreground-muted)]">
            {study.numberOfSeries}s / {study.numberOfInstances}i
          </span>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {isReturned ? (
          <Badge variant="warning">
            {WORKLIST_STATUS_LABELS.RETURNED}
          </Badge>
        ) : (
          <Badge variant={getStatusBadgeVariant(study.status)}>
            {STUDY_STATUS_LABELS[study.status]}
          </Badge>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {assigneeName ? (
          <span className="text-sm font-medium">{assigneeName}</span>
        ) : (
          <span className="text-[var(--foreground-muted)] text-sm">-</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1 whitespace-nowrap">
          {/* Assign/Reassign button - hidden if study is already read (COMPLETED) */}
          {canAssign && study.status !== 'COMPLETED' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAssign(study)}
              title={isAssigned || isReturned ? 'Reasignar a radiólogo' : 'Asignar a radiólogo'}
              className={isReturned ? 'text-orange-600 dark:text-orange-400 border-orange-300 hover:bg-orange-50 dark:bg-orange-950/40' : 'text-[var(--primary)]'}
            >
              <UserPlus className="h-4 w-4 mr-1" />
              {isAssigned || isReturned ? 'Reasignar' : 'Asignar'}
            </Button>
          )}
          <Link to={`/studies/${study.id}`}>
            <Button variant="ghost" size="sm" title="Ver detalles">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
          <Link to={`/viewer/${study.id}`}>
            <Button variant="ghost" size="sm" title="Abrir visor">
              <FileImage className="h-4 w-4" />
            </Button>
          </Link>
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(study)}
              disabled={isDeleting}
              title="Eliminar"
              className="text-[var(--destructive)] hover:text-[var(--destructive)]"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <FileImage className="h-12 w-12 text-[var(--foreground-muted)] mb-4" />
      <h3 className="text-lg font-semibold text-[var(--foreground)]">
        No se encontraron estudios
      </h3>
      <p className="text-[var(--foreground-muted)] mt-1">
        Intenta ajustar los filtros de búsqueda
      </p>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      <p className="text-[var(--foreground-muted)] mt-4">Cargando estudios...</p>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-[var(--destructive)] mb-4" />
      <h3 className="text-lg font-semibold text-[var(--foreground)]">Error al cargar</h3>
      <p className="text-[var(--foreground-muted)] mt-1">{message}</p>
      <Button onClick={onRetry} className="mt-4">
        Reintentar
      </Button>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function StudiesPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showFilters, setShowFilters] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [studyToDelete, setStudyToDelete] = useState<StudyWithPatient | null>(null)
  const [studyToAssign, setStudyToAssign] = useState<StudyWithPatient | null>(null)

  // Permission check - only ADMIN and SUPER_ADMIN can delete
  const canDelete = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.SUPER_ADMIN
  // Permission check - ADMIN, SUPER_ADMIN, and TECHNICIAN can assign
  const canAssign = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.SUPER_ADMIN || user?.role === USER_ROLES.TECHNICIAN

  // Fetch dynamic modalities from database
  const { data: modalitiesData } = useActiveModalities()
  const modalityOptions = useMemo(() =>
    modalitiesData?.modalities?.map((mod) => ({
      value: mod.code,
      label: mod.name,
    })) || [],
    [modalitiesData]
  )

  // Fetch all worklist items to identify assigned studies and get assignee info
  const { data: worklistData } = useWorklist({ limit: 1000 })
  // Studies that actually have an assignedTo (radiologist) are considered assigned
  const assignedStudyIds = useMemo(() => {
    if (!worklistData?.items) return new Set<string>()
    return new Set(
      worklistData.items
        .filter(item => item.status !== 'CANCELLED' && item.assignedTo)
        .map(item => item.studyId)
    )
  }, [worklistData])

  // Map studyId to assignee name for display (only if actually assigned to someone)
  const studyAssigneeMap = useMemo(() => {
    if (!worklistData?.items) return new Map<string, string>()
    const map = new Map<string, string>()
    worklistData.items
      .filter(item => item.status !== 'CANCELLED' && item.assignedTo && item.assignedToName)
      .forEach(item => {
        map.set(item.studyId, item.assignedToName!)
      })
    return map
  }, [worklistData])

  // Fetch returned items to identify returned studies
  const { data: returnedItems } = useReturnedItems()
  const returnedStudyIds = useMemo(() => {
    if (!returnedItems) return new Set<string>()
    return new Set(returnedItems.map(item => item.studyId))
  }, [returnedItems])

  // Filters from URL
  const patientId = searchParams.get('patientId') || ''
  const modality = searchParams.get('modality') || ''
  const status = searchParams.get('status') || ''
  const dateFrom = searchParams.get('dateFrom') || ''
  const dateTo = searchParams.get('dateTo') || ''
  const accessionNumber = searchParams.get('accessionNumber') || ''
  const referringPhysician = searchParams.get('referringPhysician') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)
  const pageSize = PAGINATION.DEFAULT_LIMIT

  // Local state for form inputs
  const [localModality, setLocalModality] = useState(modality)
  const [localStatus, setLocalStatus] = useState(status)
  const [localDateFrom, setLocalDateFrom] = useState(dateFrom)
  const [localDateTo, setLocalDateTo] = useState(dateTo)
  const [localAccessionNumber, setLocalAccessionNumber] = useState(accessionNumber)
  const [localReferringPhysician, setLocalReferringPhysician] = useState(referringPhysician)

  // Build filters object
  const filters: StudySearchFilters = useMemo(
    () => ({
      patientId: patientId || undefined,
      modality: modality || undefined,
      status: status as StudyStatus || undefined,
      studyDateFrom: dateFrom || undefined,
      studyDateTo: dateTo || undefined,
      accessionNumber: accessionNumber || undefined,
      referringPhysician: referringPhysician || undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [patientId, modality, status, dateFrom, dateTo, accessionNumber, referringPhysician, page, pageSize]
  )

  // Queries
  const { data, isLoading, isError, error, refetch } = useStudies(filters)
  const deleteStudyMutation = useDeleteStudy()

  // Calculated values
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams()
    // Preserve patientId filter if present
    if (patientId) params.set('patientId', patientId)
    if (localModality) params.set('modality', localModality)
    if (localStatus) params.set('status', localStatus)
    if (localDateFrom) params.set('dateFrom', localDateFrom)
    if (localDateTo) params.set('dateTo', localDateTo)
    if (localAccessionNumber) params.set('accessionNumber', localAccessionNumber)
    if (localReferringPhysician) params.set('referringPhysician', localReferringPhysician)
    params.set('page', '1')
    setSearchParams(params)
  }

  const handleClearFilters = () => {
    setLocalModality('')
    setLocalStatus('')
    setLocalDateFrom('')
    setLocalDateTo('')
    setLocalAccessionNumber('')
    setLocalReferringPhysician('')
    // Preserve patientId filter if present, otherwise clear all
    if (patientId) {
      setSearchParams({ patientId, page: '1' })
    } else {
      setSearchParams({ page: '1' })
    }
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  const handleDeleteClick = (study: StudyWithPatient) => {
    setStudyToDelete(study)
  }

  const handleAssignClick = (study: StudyWithPatient) => {
    setStudyToAssign(study)
  }

  const handleConfirmDelete = () => {
    if (studyToDelete) {
      deleteStudyMutation.mutate(studyToDelete.id, {
        onSuccess: () => {
          setStudyToDelete(null)
        },
      })
    }
  }

  const handleCancelDelete = () => {
    setStudyToDelete(null)
  }

  const hasFilters = patientId || modality || status || dateFrom || dateTo || accessionNumber || referringPhysician

  // Get first study's patient name if filtering by patientId (for display)
  const patientNameFromStudy = patientId && data?.studies.length ? data.studies[0]?.patientName : null

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Patient Filter Banner */}
      {patientId && (
        <div className="flex items-center justify-between bg-[var(--primary)]/10 border border-[var(--primary)]/20 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-[var(--primary)]" />
            <div>
              <p className="text-sm font-medium text-[var(--foreground)]">
                Mostrando estudios de: <span className="text-[var(--primary)]">{patientNameFromStudy || 'Paciente'}</span>
              </p>
              <p className="text-xs text-[var(--foreground-muted)]">
                Filtrado por paciente específico
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/patients/${patientId}`}>
              <Button variant="outline" size="sm">
                Ver Paciente
              </Button>
            </Link>
            <Link to="/studies">
              <Button variant="ghost" size="sm">
                <X className="h-4 w-4 mr-1" />
                Ver todos
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Header */}
      {/* En un telefono el titulo y los botones no caben en una linea y
          empujaban la pagina. Se permite que bajen de linea en vez de
          desbordar: `flex-wrap` mas `gap`, que es lo que hace falta. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">Estudios</h1>
          <p className="text-[var(--foreground-muted)]">
            {patientId ? 'Estudios del paciente' : 'Gestiona los estudios de imágenes médicas'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setShowUploadModal(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Cargar Estudio
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtros
            {hasFilters && (
              <Badge variant="default" className="ml-2">
                {Object.values({ modality, status, dateFrom, dateTo, accessionNumber, referringPhysician }).filter(Boolean).length}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Search Filters */}
      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                    Modalidad
                  </label>
                  <Select
                    options={modalityOptions}
                    value={localModality}
                    onChange={(e) => setLocalModality(e.target.value)}
                    placeholder="Todas las modalidades"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                    Estado
                  </label>
                  <Select
                    options={STATUS_OPTIONS}
                    value={localStatus}
                    onChange={(e) => setLocalStatus(e.target.value)}
                    placeholder="Todos los estados"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                    Nº Acceso
                  </label>
                  <Input
                    placeholder="Número de acceso..."
                    value={localAccessionNumber}
                    onChange={(e) => setLocalAccessionNumber(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                    Fecha desde
                  </label>
                  <Input
                    type="date"
                    value={localDateFrom}
                    onChange={(e) => setLocalDateFrom(e.target.value)}
                    leftIcon={<Calendar className="h-4 w-4" />}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                    Fecha hasta
                  </label>
                  <Input
                    type="date"
                    value={localDateTo}
                    onChange={(e) => setLocalDateTo(e.target.value)}
                    leftIcon={<Calendar className="h-4 w-4" />}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                    Médico remitente
                  </label>
                  <Input
                    placeholder="Nombre del médico..."
                    value={localReferringPhysician}
                    onChange={(e) => setLocalReferringPhysician(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                {hasFilters && (
                  <Button type="button" variant="outline" onClick={handleClearFilters}>
                    <X className="h-4 w-4 mr-2" />
                    Limpiar
                  </Button>
                )}
                <Button type="submit">
                  <Search className="h-4 w-4 mr-2" />
                  Buscar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState
              message={error?.message || 'Error desconocido'}
              onRetry={() => refetch()}
            />
          ) : !data?.studies.length ? (
            <EmptyState />
          ) : (
            <>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Paciente</TableHead>
                    <TableHead className="whitespace-nowrap">Estudio</TableHead>
                    <TableHead className="whitespace-nowrap">Modalidad</TableHead>
                    <TableHead className="whitespace-nowrap">Fecha</TableHead>
                    <TableHead className="whitespace-nowrap">Estado</TableHead>
                    <TableHead className="whitespace-nowrap">Asignado a</TableHead>
                    <TableHead className="whitespace-nowrap">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.studies.map((study) => (
                    <StudyRow
                      key={study.id}
                      study={study as StudyWithPatient}
                      onDelete={handleDeleteClick}
                      onAssign={handleAssignClick}
                      isDeleting={deleteStudyMutation.isPending && studyToDelete?.id === study.id}
                      canDelete={canDelete}
                      canAssign={canAssign}
                      isReturned={returnedStudyIds.has(study.id)}
                      isAssigned={assignedStudyIds.has(study.id)}
                      assigneeName={studyAssigneeMap.get(study.id) ?? null}
                    />
                  ))}
                </TableBody>
              </Table>
              </div>

              {/* Pagination */}
              <PaginationBar
                  currentPage={page}
                  pageSize={pageSize}
                  total={data.total}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <UploadDicomModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        onSuccess={() => refetch()}
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={!!studyToDelete} onOpenChange={(open) => !open && handleCancelDelete()}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[var(--destructive)]">
              <Trash2 className="h-5 w-5" />
              Eliminar Estudio
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. Se eliminarán permanentemente todos los datos e imágenes asociadas.
            </DialogDescription>
          </DialogHeader>

          {studyToDelete && (
            <div className="py-4 space-y-2">
              <p className="text-sm">
                <span className="font-medium">Paciente:</span>{' '}
                {studyToDelete.patientName} ({studyToDelete.patientDocumentType} {studyToDelete.patientDocumentNumber})
              </p>
              <p className="text-sm">
                <span className="font-medium">Estudio:</span>{' '}
                {studyToDelete.studyDescription || 'Sin descripción'}
              </p>
              <p className="text-sm">
                <span className="font-medium">Modalidad:</span>{' '}
                {studyToDelete.modality}
              </p>
              <p className="text-sm">
                <span className="font-medium">Fecha:</span>{' '}
                {formatStudyDate(studyToDelete.studyDate)}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={deleteStudyMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteStudyMutation.isPending}
            >
              {deleteStudyMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Study Modal */}
      <AssignStudyModal
        open={!!studyToAssign}
        onOpenChange={(open) => !open && setStudyToAssign(null)}
        studyId={studyToAssign?.id ?? null}
        studyDescription={studyToAssign?.studyDescription || undefined}
        onSuccess={() => refetch()}
      />
    </div>
  )
}
